import posthog from 'posthog-js';
import { useEffect, useState } from 'react';

let initialized = false;
const flagListeners = new Set<() => void>();
let posthogFlagsHookAttached = false;

/**
 * Subscribe à PostHog `onFeatureFlags` pour notifier tous les `useFlag` montés.
 * Idempotent : la subscription est attachée une seule fois après init.
 */
function attachPosthogFlagsHook(): void {
  if (posthogFlagsHookAttached || !initialized) return;
  posthogFlagsHookAttached = true;
  posthog.onFeatureFlags(() => {
    flagListeners.forEach((cb) => cb());
  });
}

/**
 * Initialise PostHog uniquement si l'utilisateur a consenti à l'analytics.
 * À appeler après lecture du store useConsent (côté client uniquement).
 */
export function initAnalytics(consentAnalytics = false): void {
  if (typeof window === 'undefined') return;

  const key = (window as unknown as { __POSTHOG_KEY?: string }).__POSTHOG_KEY;
  const host = (window as unknown as { __POSTHOG_HOST?: string }).__POSTHOG_HOST;
  if (!key) return;

  if (!consentAnalytics) {
    // Opt-out si déjà initialisé
    if (initialized) posthog.opt_out_capturing();
    return;
  }

  if (!initialized) {
    posthog.init(key, {
      api_host: host ?? 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,
      session_recording: { maskAllInputs: true },
      persistence: 'localStorage',
      opt_out_capturing_by_default: false,
    });
    initialized = true;
    attachPosthogFlagsHook();
  } else {
    posthog.opt_in_capturing();
    attachPosthogFlagsHook();
  }
}

export function captureEvent(event: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !initialized) return;
  posthog.capture(event, props);
}

export function identify(distinctId: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !initialized) return;
  posthog.identify(distinctId, props);
}

export function isFeatureEnabled(flag: string): boolean {
  if (typeof window === 'undefined' || !initialized) return false;
  return posthog.isFeatureEnabled(flag) === true;
}

/**
 * Récupère la variante d'un feature flag multivarié PostHog.
 * Retourne `undefined` si PostHog inactif (SSR / pas de consentement / flag non chargé).
 * L'appel enregistre automatiquement l'exposition (`$feature_flag_called`).
 */
export function getFeatureFlagVariant(flag: string): string | boolean | undefined {
  if (typeof window === 'undefined' || !initialized) return undefined;
  return posthog.getFeatureFlag(flag);
}

/**
 * Feature flags gérés par PostHog.
 * `new_design_system` — rollout progressif 0 % → 10 % → 50 % → 100 %.
 * Créer ce flag dans le dashboard PostHog avec rollout 0 % au départ.
 */
export const FLAGS = {
  NEW_DESIGN_SYSTEM: 'new_design_system',
} as const;

export type FeatureFlag = (typeof FLAGS)[keyof typeof FLAGS];

/**
 * Hook React qui s'abonne aux feature flags PostHog et **re-render** quand
 * les flags sont reçus ou rafraîchis.
 *
 * Côté SSR : retourne `false` (PostHog inactif).
 * Côté client avant initialisation : retourne `false` puis se met à jour
 * automatiquement dès que `initAnalytics(true)` a été appelé et que les flags
 * arrivent du serveur PostHog.
 *
 * La version précédente appelait simplement `isFeatureEnabled` au render, ce
 * qui retournait `false` au premier render (flags pas encore chargés) et ne
 * se mettait jamais à jour ensuite — le flag `new_design_system` n'était donc
 * jamais activé côté client, peu importe le rollout PostHog.
 */
export function useFlag(flag: FeatureFlag): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => isFeatureEnabled(flag));

  useEffect(() => {
    const update = () => setEnabled(isFeatureEnabled(flag));
    update();
    flagListeners.add(update);
    attachPosthogFlagsHook();
    return () => {
      flagListeners.delete(update);
    };
  }, [flag]);

  return enabled;
}

/**
 * Hook d'expérimentation A/B multivariée basé sur les feature flags PostHog.
 *
 * - `variant` vaut `fallback` tant que les flags ne sont pas chargés (ou SSR / pas de consentement).
 * - Se met à jour automatiquement dès réception des flags PostHog.
 * - L'exposition est trackée par PostHog (`$feature_flag_called`) au premier appel.
 *
 * @example
 *   const { variant } = useExperiment('cta_wording', 'control');
 *   return <Button>{variant === 'urgent' ? 'Démarrer maintenant' : 'Commencer'}</Button>;
 */
export function useExperiment<V extends string = string>(
  flagKey: string,
  // NoInfer empêche TS de déduire V depuis le littéral fallback (ex. 'sticky'),
  // sinon `variant` serait figé sur ce littéral et toute comparaison à une autre
  // variante ('control', …) deviendrait une erreur TS2367. V reste donc `string`
  // par défaut, ce qui autorise les comparaisons inter-variantes côté appelant.
  fallback: NoInfer<V>,
): { variant: V; isReady: boolean } {
  const read = (): { variant: V; isReady: boolean } => {
    const raw = getFeatureFlagVariant(flagKey);
    if (raw === undefined) return { variant: fallback, isReady: false };
    if (typeof raw === 'boolean') {
      return { variant: (raw ? 'test' : 'control') as V, isReady: true };
    }
    return { variant: raw as V, isReady: true };
  };

  const [state, setState] = useState(read);

  useEffect(() => {
    const update = () => setState(read());
    update();
    flagListeners.add(update);
    attachPosthogFlagsHook();
    return () => {
      flagListeners.delete(update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagKey, fallback]);

  return state;
}

/** Enregistre une conversion liée à une expérience (à appeler au moment clé). */
export function trackExperimentConversion(
  flagKey: string,
  variant: string,
  goal: string,
  props?: Record<string, unknown>,
): void {
  captureEvent('experiment_conversion', {
    experiment: flagKey,
    variant,
    goal,
    ...props,
  });
}
